import React, { memo } from 'react';
import {
    Box,
    Typography,
    Alert,
    Button,
    Skeleton,
} from '@mui/material';
import Grid from '@mui/material/Grid';

import { useTeacherDashboard } from '../../hooks/useDashboard';
import { useSubscription } from '../../context/SubscriptionContext';
import {
    MyAttendanceRecordCard,
    LeaveBalanceCard,
    DashboardRefreshButton,
} from '../../components/dashboard';

/**
 * Employee Dashboard
 *
 * For every staff member who isn't a Teacher / School Admin / System Admin —
 * a Receptionist, Accountant, etc. They still have attendance and leave
 * balances, so we surface those two cards. Teacher-specific widgets (today's
 * schedule, pending marks) are omitted because they make no sense here; the
 * BE response will carry empty defaults for those fields, which we ignore.
 *
 * Data and components are reused verbatim from the Teacher dashboard — same
 * `useTeacherDashboard()` hook, same `<MyAttendanceRecordCard>` and
 * `<LeaveBalanceCard>` components. No card was reimplemented for this view.
 */
const EmployeeDashboard: React.FC = memo(() => {
    const { data, loading, error, refetch } = useTeacherDashboard();
    const { hasFeature } = useSubscription();

    const showAttendanceCard = hasFeature('attendance');
    const showLeaveCard      = hasFeature('leaves');

    if (error && !loading) {
        return (
            <Box>
                <Alert
                    severity="error"
                    action={
                        <Button color="inherit" size="small" onClick={refetch}>
                            Retry
                        </Button>
                    }
                    sx={{ borderRadius: 2 }}
                >
                    {error}
                </Alert>
            </Box>
        );
    }

    if (loading) {
        return (
            <Box>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                    <Box>
                        <Skeleton variant="text" width={150} height={40} />
                        <Skeleton variant="text" width={280} height={20} />
                    </Box>
                </Box>
                <Grid container spacing={2}>
                    {showAttendanceCard && (
                        <Grid size={{ xs: 12, sm: 6, lg: showLeaveCard ? 5 : 12 }}>
                            <Skeleton variant="rounded" height={320} sx={{ borderRadius: 3 }} />
                        </Grid>
                    )}
                    {showLeaveCard && (
                        <Grid size={{ xs: 12, sm: 6, lg: showAttendanceCard ? 7 : 12 }}>
                            <Skeleton variant="rounded" height={320} sx={{ borderRadius: 3 }} />
                        </Grid>
                    )}
                </Grid>
            </Box>
        );
    }

    return (
        <Box>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Box>
                    <Typography variant="h4" fontWeight={600} color="text.primary" gutterBottom>
                        Dashboard
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Welcome back! Here's your overview for today.
                    </Typography>
                </Box>
                <DashboardRefreshButton onClick={refetch} />
            </Box>

            {(showAttendanceCard || showLeaveCard) && (
                <Grid container spacing={2}>
                    {showAttendanceCard && (
                        <Grid size={{ xs: 12, sm: 6, lg: showLeaveCard ? 5 : 12 }}>
                            <MyAttendanceRecordCard
                                data={data?.attendanceSummary}
                                currentMonth={data?.currentMonth}
                                loading={loading}
                            />
                        </Grid>
                    )}
                    {showLeaveCard && (
                        <Grid size={{ xs: 12, sm: 6, lg: showAttendanceCard ? 7 : 12 }}>
                            <LeaveBalanceCard
                                data={data?.leaveBalance}
                                loading={loading}
                            />
                        </Grid>
                    )}
                </Grid>
            )}

            {!showAttendanceCard && !showLeaveCard && (
                <Box
                    sx={{
                        p: 4,
                        textAlign: 'center',
                        borderRadius: 2,
                        border: '1px dashed',
                        borderColor: 'divider',
                    }}
                >
                    <Typography variant="body2" color="text.secondary">
                        Your school hasn't enabled the Attendance or Leaves modules yet.
                    </Typography>
                </Box>
            )}
        </Box>
    );
});

EmployeeDashboard.displayName = 'EmployeeDashboard';

export default EmployeeDashboard;
